import IconButton from '@/global/components/Button/IconButton'
import { EXPANDABLE_COL } from '@/global/components/DataGrid/components/ExpandCell'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { cn } from '@/lib/utils'
import { hoursToMinutes, minutesToHours } from '@acme/common'
import { Row } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { ListIcon } from 'lucide-react'
import React from 'react'
import { linhasFolhaDoGrupo } from '../PontoDiario/contrato-agrupamento'
import { LinhaExplodidaDSR } from './explodir-linhas'
import {
    CAMPO_POR_NIVEL,
    LABEL_NIVEL,
    NivelDSR,
    ORDEM_NIVEIS_CLIENTE,
    ORDEM_NIVEIS_FUNCIONARIO,
} from './niveis-agrupamento'

export { CAMPO_POR_NIVEL }

const NIVEL_POR_CAMPO = Object.fromEntries(
    Object.entries(CAMPO_POR_NIVEL).map(([nivel, campo]) => [campo, nivel]),
) as Record<string, NivelDSR>

const CHIP_POR_PROFUNDIDADE: Record<number, string> = {
    0: 'bg-primary-800',
    1: 'bg-primary-700',
    2: 'bg-primary-600',
    3: 'bg-primary-500',
}

export function somarValorDSR(
    folhas: Row<LinhaExplodidaDSR>[],
    dsrExibeHoras?: string,
): string {
    if (dsrExibeHoras === 'S') {
        const totalMin = folhas.reduce(
            (acc, r) => acc + hoursToMinutes(r.original.valor || '00:00'),
            0,
        )
        return minutesToHours(totalMin)
    }
    const totalDias = folhas.reduce(
        (acc, r) =>
            acc +
            (Number(String(r.original.valor || '').replace(/\D/g, '')) || 0),
        0,
    )
    return `${totalDias} dia(s)`
}

function ColunaVisual({
    tableRow,
    value,
}: {
    tableRow?: Row<LinhaExplodidaDSR>
    value?: string
}) {
    if (!tableRow) return null

    if (tableRow.getIsGrouped()) {
        const campoGrupo = tableRow.groupingColumnId as string
        const nivel = NIVEL_POR_CAMPO[campoGrupo]
        const leaf = linhasFolhaDoGrupo(tableRow)[0]?.original

        const labelGrupo = leaf
            ? campoGrupo === CAMPO_POR_NIVEL.centroCusto
                ? leaf._centroCustoNome
                : campoGrupo === CAMPO_POR_NIVEL.local
                  ? leaf._localNome
                  : campoGrupo === CAMPO_POR_NIVEL.funcionario
                    ? leaf._funcionarioNome
                    : campoGrupo === CAMPO_POR_NIVEL.data
                      ? leaf._dataLabel
                      : tableRow.getValue<string>(campoGrupo)
            : tableRow.getValue<string>(campoGrupo)

        return (
            <span
                className="flex items-center gap-2"
                style={{ paddingLeft: tableRow.depth * 24 }}
            >
                <span
                    className={cn(
                        CHIP_POR_PROFUNDIDADE[tableRow.depth] ??
                            'bg-primary-500',
                        'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white',
                    )}
                >
                    {LABEL_NIVEL[nivel] ?? campoGrupo}
                </span>
                <span className="font-bold">{labelGrupo}</span>
                <span className="text-primary-600 text-xs font-normal">
                    ({linhasFolhaDoGrupo(tableRow).length})
                </span>
            </span>
        )
    }

    return <span>{value ?? ''}</span>
}

export function colunasDsrCliente(
    niveisSelecionados: Set<NivelDSR>,
    dsrExibeHoras?: string,
): GridColDefPro<LinhaExplodidaDSR>[] {
    if (niveisSelecionados.size === 0) {
        return [
            {
                field: 'Funcionario',
                headerName: 'Funcionário',
                flex: 1,
                minWidth: 200,
                valueGetter: ({ value, row }: any) =>
                    row?._funcionarioNome ??
                    value?.nome ??
                    row?.Funcionario?.nome ??
                    '',
            },
            {
                field: 'valor',
                headerName: 'Valor descontado',
                width: 200,
                minWidth: 100,
            },
            {
                ...optionsColumn,
                field: '__detalhes',
                renderCell: ({ row, id }: any) => {
                    const funcId =
                        row?.funcionarioId ?? row?.Funcionario?.id ?? id
                    return (
                        <IconButton
                            href={`${funcId}/detalhes`}
                            title="Listar descontos detalhados"
                        >
                            <ListIcon />
                        </IconButton>
                    )
                },
            },
            {
                field: 'locais',
                headerName: 'Locais',
                width: 200,
                valueGetter: ({ row }: any) =>
                    row?._localNome ??
                    row?.Funcionario?.FuncionarioLocais?.map(
                        (e: any) => e.Local?.nomeLocal,
                    )?.join(', ') ??
                    '',
                hide: true,
            },
        ]
    }

    const colunasOcultas: GridColDefPro<LinhaExplodidaDSR>[] =
        ORDEM_NIVEIS_CLIENTE.filter(n => niveisSelecionados.has(n)).map(n => ({
            field: CAMPO_POR_NIVEL[n] as any,
            headerName: LABEL_NIVEL[n],
            hide: true,
        }))

    const colunasVisiveis: GridColDefPro<LinhaExplodidaDSR>[] = [
        EXPANDABLE_COL,
        {
            field: 'Funcionario',
            headerName: 'Funcionário',
            flex: 1,
            minWidth: 200,
            valueGetter: ({ row }: any) =>
                row?._funcionarioNome ?? row?.Funcionario?.nome ?? '',
            renderCell: ({ tableRow, value }: any) => (
                <ColunaVisual tableRow={tableRow} value={value} />
            ),
        },
        {
            field: 'valor',
            headerName: 'Valor descontado',
            width: 200,
            minWidth: 100,
            renderCell: ({ tableRow, value }: any) => {
                if (tableRow?.getIsGrouped()) {
                    const folhas = linhasFolhaDoGrupo(tableRow)
                    return (
                        <span className="font-bold">
                            {somarValorDSR(folhas, dsrExibeHoras)}
                        </span>
                    )
                }
                return <span>{value ?? ''}</span>
            },
        },
        {
            ...optionsColumn,
            field: '__detalhes',
            renderCell: ({ tableRow, row }: any) => {
                if (tableRow?.getIsGrouped()) return null
                const funcId = row?.funcionarioId ?? row?.Funcionario?.id
                return (
                    <IconButton
                        href={`${funcId}/detalhes`}
                        title="Listar descontos detalhados"
                    >
                        <ListIcon />
                    </IconButton>
                )
            },
        },
    ]

    return [...colunasOcultas, ...colunasVisiveis]
}

export function colunasDsrFuncionario(
    niveisSelecionados: Set<NivelDSR>,
    dsrExibeHoras?: string,
): GridColDefPro<LinhaExplodidaDSR>[] {
    if (niveisSelecionados.size === 0) {
        return [
            {
                field: 'data',
                headerName: 'Data',
                flex: 1,
                minWidth: 110,
                type: 'date',
                valueFormatter: ({ value }: any) =>
                    value ? dayjs(value).format('DD/MM/YYYY - dddd') : '',
            },
            {
                field: 'valor',
                headerName: 'Valor descontado',
                flex: 1,
                minWidth: 100,
            },
        ]
    }

    const colunasOcultas: GridColDefPro<LinhaExplodidaDSR>[] =
        ORDEM_NIVEIS_FUNCIONARIO.filter(n => niveisSelecionados.has(n)).map(
            n => ({
                field: CAMPO_POR_NIVEL[n] as any,
                headerName: LABEL_NIVEL[n],
                hide: true,
            }),
        )

    const colunasVisiveis: GridColDefPro<LinhaExplodidaDSR>[] = [
        EXPANDABLE_COL,
        {
            field: 'data',
            headerName: 'Data',
            flex: 1,
            minWidth: 110,
            type: 'date',
            valueGetter: ({ row }: any) => row?._dataLabel ?? '',
            renderCell: ({ tableRow, value }: any) => (
                <ColunaVisual tableRow={tableRow} value={value} />
            ),
        },
        {
            field: 'valor',
            headerName: 'Valor descontado',
            flex: 1,
            minWidth: 100,
            renderCell: ({ tableRow, value }: any) => {
                if (tableRow?.getIsGrouped()) {
                    const folhas = linhasFolhaDoGrupo(tableRow)
                    return (
                        <span className="font-bold">
                            {somarValorDSR(folhas, dsrExibeHoras)}
                        </span>
                    )
                }
                return <span>{value ?? ''}</span>
            },
        },
    ]

    return [...colunasOcultas, ...colunasVisiveis]
}
